import React, { useState } from "react";
import {
  IonPage,
  IonHeader,
  IonToolbar,
  IonTitle,
  IonContent,
  IonButtons,
  IonMenuButton,
  IonLoading,
} from "@ionic/react";
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
  TextField,
  Button,
  IconButton,
  Card,
  CardContent,
  Grid,
  Chip,
  Avatar,
  LinearProgress,
  Divider,
  InputAdornment,
  TableContainer,
  TablePagination,
  alpha,
  useTheme,
  Modal,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
} from "@mui/material";
import {
  Search,
  ArrowBack,
  Visibility,
  TrendingUp,
  Inventory,
  AttachMoney,
  Scale,
  CalendarToday,
  FilterList,
  Refresh,
  Close,
} from "@mui/icons-material";
import { baseUrl, config, requestID } from "../../../common/utils";
import axios from "axios";

const StockStatus: React.FC = () => {
  const theme = useTheme();
  const [selectedProduct, setSelectedProduct] = useState<any>(null);
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [filteredProducts, setFilteredProducts] = useState<any[]>([]);
  const [logs, setLogs] = useState<any[]>([]);
  const [logType, setLogType] = useState<"in" | "out" | null>(null);
  const [showProductTable, setShowProductTable] = useState(false);
  const [status, setStatus] = useState({ loading: false, error: false });
  const [dateError, setDateError] = useState({ start: false, end: false });
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [searchTerm, setSearchTerm] = useState("");
  const [modalOpen, setModalOpen] = useState(false);
  const [loadingPD, setLoadingPD] = useState(false);

  const handleFilterProducts = async () => {
    const startMissing = !startDate;
    const endMissing = !endDate;

    if (startMissing || endMissing) {
      setDateError({ start: startMissing, end: endMissing });
      return;
    }

    setDateError({ start: false, end: false });
    await getStockStatus();
  };

  async function getStockStatus() {
    setStatus({ loading: true, error: false });

    try {
      const formData = new FormData();
      formData.append("requestID", requestID());
      formData.append("start_date", startDate);
      formData.append("end_date", endDate);

      const response = await axios.post(
        baseUrl() + "Orderhistorys/stockStatusReportAnalysis1",
        formData,
        config
      );
      console.log(response);

      setFilteredProducts(response.data.data || []);
      setShowProductTable(true);
      setSelectedProduct(null);
      setLogs([]);
      setStatus({ loading: false, error: false });
    } catch (error) {
      console.error("API call failed:", error);
      setStatus({ loading: false, error: true });
    }
  }

  async function getStockDetails(productID: string) {
    setLoadingPD(true);
    setStatus({ loading: true, error: false });

    try {
      const formData = new FormData();
      formData.append("requestID", requestID());
      formData.append("start_date", startDate);
      formData.append("end_date", endDate);
      formData.append("product_id", productID);

      const response = await axios.post(
        baseUrl() + "Orderhistorys/stockStatusReportAnalysis2",
        formData,
        config
      )
      console.log(response)
      const data = response.data?.data || {};
      const outLogs = data.out || [];
      const inLogs = data.in || [];

      const selectedLogType =
        outLogs.length > 0 ? "out" : inLogs.length > 0 ? "in" : null;

      setLogs(selectedLogType === "out" ? outLogs : inLogs);
      setLogType(selectedLogType);
      setStatus({ loading: false, error: false });
      setLoadingPD(false);
    } catch (error) {
      console.error("API call failed:", error);
      setStatus({ loading: false, error: true });
      setLoadingPD(false);
    }
  }

  const handleProductClick = async (product: any) => {
    setSelectedProduct(product);
    setModalOpen(true);
    await getStockDetails(product.product_id);
  };

  const handleCloseModal = () => {
    setModalOpen(false);
    setSelectedProduct(null);
    setLogs([]);
    setLogType(null);
  };

  const handleRefreshLogs = async () => {
    if (selectedProduct) {
      await getStockDetails(selectedProduct.product_id);
    }
  };

  const getProductName = (productId: string) => {
    const product = filteredProducts.find((p) => p.product_id === productId);
    return product?.product_name || "N/A";
  };

  const filteredData = filteredProducts.filter((product) =>
    product.product_name?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const formatCurrency = (amount: number) => {
    return `₦${Number(amount).toLocaleString()}`;
  };

  const formatNumber = (num: number) => {
    return num.toLocaleString(undefined, { 
      minimumFractionDigits: 2, 
      maximumFractionDigits: 2 
    });
  };

  return (
    <IonPage>
      <IonHeader>
        <IonToolbar style={{ background: theme.palette.primary.main }}>
          <IonButtons slot="start">
            <IonMenuButton style={{ color: "white" }} />
          </IonButtons>
          <IonTitle style={{ color: "white" }}>Stock Status Report</IonTitle>
        </IonToolbar>
      </IonHeader>

      <IonContent className="ion-padding" style={{ backgroundColor: theme.palette.background.default }}>
        <IonLoading isOpen={status.loading} message={"Please wait..."} spinner="crescent" />

        {/* Header Section */}
        <Box sx={{ mb: 4 }}>
          <Typography variant="h4" fontWeight="600" color="text.primary" gutterBottom>
            Stock Status Dashboard
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Monitor inventory levels and track stock movements
          </Typography>
        </Box>

        {/* Filter Card */}
        <Card sx={{ mb: 3, borderRadius: 2, boxShadow: theme.shadows[3] }}>
          <CardContent>
            <Box display="flex" alignItems="center" gap={1} mb={2}>
              <FilterList color="primary" />
              <Typography variant="h6" fontWeight="500">
                Filter Options
              </Typography>
            </Box>
            
            <Grid container spacing={3} alignItems="center">
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  label="Start Date"
                  type="date"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                  InputLabelProps={{ shrink: true }}
                  error={dateError.start}
                  helperText={dateError.start ? "Start date is required" : ""}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <CalendarToday fontSize="small" color="action" />
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>
              
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  label="End Date"
                  type="date"
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                  InputLabelProps={{ shrink: true }}
                  error={dateError.end}
                  helperText={dateError.end ? "End date is required" : ""}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <CalendarToday fontSize="small" color="action" />
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>
              
              <Grid item xs={12} md={4}>
                <Button
                  fullWidth
                  variant="contained"
                  size="large"
                  onClick={handleFilterProducts}
                  startIcon={<TrendingUp />}
                  sx={{ height: 56 }}
                >
                  Generate Report
                </Button>
              </Grid>
            </Grid>
          </CardContent>
        </Card>

        {/* Search Box */}
        {showProductTable && (
          <Card sx={{ mb: 2, borderRadius: 2, boxShadow: theme.shadows[2] }}>
            <CardContent>
              <TextField
                fullWidth
                placeholder="Search products..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Search color="action" />
                    </InputAdornment>
                  ),
                }}
              />
            </CardContent>
          </Card>
        )}

        {/* Product Table */}
        {showProductTable && (
          <Card sx={{ borderRadius: 2, boxShadow: theme.shadows[3] }}>
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow sx={{ backgroundColor: alpha(theme.palette.primary.main, 0.08) }}>
                    <TableCell sx={{ fontWeight: 600, color: 'text.primary' }}>Product</TableCell>
                    <TableCell sx={{ fontWeight: 600, color: 'text.primary' }} align="center">Unit Price</TableCell>
                    <TableCell sx={{ fontWeight: 600, color: 'text.primary' }} align="center">Total Supplied</TableCell>
                    <TableCell sx={{ fontWeight: 600, color: 'text.primary' }} align="center">Total Consumed</TableCell>
                    <TableCell sx={{ fontWeight: 600, color: 'text.primary' }} align="center">Sold Amount</TableCell>
                    <TableCell sx={{ fontWeight: 600, color: 'text.primary' }} align="center">Current Balance</TableCell>
                    <TableCell sx={{ fontWeight: 600, color: 'text.primary' }} align="center">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredData.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map((product) => (
                    <TableRow key={`${product.product_id}_${product.id}_${product.product_name}`} hover sx={{ '&:hover': { backgroundColor: alpha(theme.palette.primary.main, 0.02) } }}>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                          <Avatar sx={{ bgcolor: alpha(theme.palette.primary.main, 0.1), color: theme.palette.primary.main }}>
                            <Inventory fontSize="small" />
                          </Avatar>
                          <Box>
                            <Typography variant="body1" fontWeight="500">
                              {product.product_name}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              ID: {product.product_id}
                            </Typography>
                          </Box>
                        </Box>
                      </TableCell>
                      <TableCell align="center">
                        <Chip label={formatCurrency(product.price)} variant="outlined" color="primary" />
                      </TableCell>
                      <TableCell align="center">
                        <Typography variant="body1" fontWeight="500">
                          {formatNumber(product.total_supplied ?? 0)}
                        </Typography>
                      </TableCell>
                      <TableCell align="center">
                        <Typography variant="body1" fontWeight="500" color="error.main">
                          {formatNumber(product.total_ordered ?? 0)}
                        </Typography>
                      </TableCell>
                      <TableCell align="center">
                        <Chip 
                          label={formatCurrency(product.total_amount_sold)} 
                          variant="filled" 
                          color="success"
                          size="small"
                        />
                      </TableCell>
                      <TableCell align="center">
                        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                          <Typography variant="body1" fontWeight="600" color="primary.main">
                            {product.current_balance}
                          </Typography>
                          <LinearProgress 
                            variant="determinate" 
                            value={Math.min((product.current_balance / (product.total_supplied || 1)) * 100, 100)}
                            sx={{ width: 60, height: 4, borderRadius: 2 }}
                          />
                        </Box>
                      </TableCell>
                      <TableCell align="center">
                        <IconButton
                          onClick={() => handleProductClick(product)}
                          disabled={!product.total_ordered || Number(product.total_ordered) < 1}
                          sx={{ 
                            color: 'primary.main',
                            '&:hover': { 
                              backgroundColor: alpha(theme.palette.primary.main, 0.1) 
                            },
                            '&.Mui-disabled': {
                              color: theme.palette.text.disabled,
                            }
                          }}
                        >
                          <Visibility />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
            
            <TablePagination
              rowsPerPageOptions={[5, 10, 25]}
              component="div"
              count={filteredData.length}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
            />
          </Card>
        )}

        {/* Empty State */}
        {!showProductTable && (
          <Card sx={{ textAlign: 'center', p: 6, borderRadius: 2, boxShadow: theme.shadows[2] }}>
            <Inventory sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
            <Typography variant="h6" color="text.secondary" gutterBottom>
              No Data Available
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              Select a date range and generate report to view stock status
            </Typography>
            <Button
              variant="contained"
              onClick={handleFilterProducts}
              startIcon={<Refresh />}
            >
              Generate Report
            </Button>
          </Card>
        )}

        {/* Product Details Modal */}
        <Dialog
          open={modalOpen}
          onClose={handleCloseModal}
          maxWidth="lg"
          fullWidth
          PaperProps={{
            sx: {
              borderRadius: 2,
              boxShadow: theme.shadows[5],
            }
          }}
        >
          <DialogTitle>
            <Box display="flex" alignItems="center" justifyContent="space-between">
              <Typography variant="h5" fontWeight="600">
                {selectedProduct?.product_name} - Transaction History
              </Typography>
              <IconButton onClick={handleCloseModal} size="small">
                <Close />
              </IconButton>
            </Box>
            <Typography variant="body2" color="text.secondary">
              Product ID: {selectedProduct?.product_id}
            </Typography>
          </DialogTitle>

         <DialogContent dividers>
  {loadingPD ? (
    <Box display="flex" justifyContent="center" alignItems="center" minHeight={200}>
      <CircularProgress size={40} />
      <Typography variant="body2" color="text.secondary" sx={{ ml: 2 }}>
        Loading transaction history...
      </Typography>
    </Box>
  ) : logs.length > 0 ? (
    <TableContainer>
      <Table>
        <TableHead>
          <TableRow sx={{ backgroundColor: alpha(theme.palette.primary.main, 0.08) }}>
            <TableCell sx={{ fontWeight: 600 }}>Date & Time</TableCell>
                        <TableCell sx={{ fontWeight: 600 }} align="center">Customer Name</TableCell>

           
            <TableCell sx={{ fontWeight: 600 }} align="center">Quantity</TableCell>
            <TableCell sx={{ fontWeight: 600 }} align="center">Total Amount</TableCell>
             <TableCell sx={{ fontWeight: 600 }} align="center">Staff Name</TableCell>

            <TableCell sx={{ fontWeight: 600 }} align="center">Type</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {logs.map((log, index) => (
            <TableRow key={`${selectedProduct?.product_id}_${index}_${log.order_date}`} hover>
              <TableCell>
                <Box>
                  <Typography variant="body1" fontWeight="500">
                    {log.order_date}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Transaction #{index + 1}
                  </Typography>
                </Box>
              </TableCell>

               <TableCell align="center">
                <Typography variant="body1" fontWeight="600" color="success.main">
                  {(log.customer_name) ? log.customer_name : 'N/A'}
                </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Phone:                   {(log.phone) ? log.phone : 'N/A'}

                  </Typography>
              </TableCell>
              <TableCell align="center">
                <Chip 
                  label={log.quantity} 
                  color={logType === "out" ? "error" : "success"}
                  variant="outlined"
                />
              </TableCell>
              <TableCell align="center">
                <Typography variant="body1" fontWeight="600" color="success.main">
                  {formatCurrency(log.total_amount)}
                </Typography>
              </TableCell>
              <TableCell align="center">
                <Typography variant="body1" fontWeight="600" color="text.primary">
                  {(log.staff_name) ? log.staff_name : 'N/A'} 
                </Typography>
              </TableCell>
              <TableCell align="center">
                <Chip 
                  label={logType} 
                  color={logType === "out" ? "error" : "success"}
                  variant="filled"
                  size="small"
                />
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  ) : (
    <Box textAlign="center" py={6}>
      <Inventory sx={{ fontSize: 48, color: 'text.secondary', mb: 2 }} />
      <Typography variant="h6" color="text.secondary" gutterBottom>
        No Transaction History
      </Typography>
      <Typography variant="body2" color="text.secondary">
        No transactions found for this product in the selected date range.
      </Typography>
    </Box>
  )}
</DialogContent>


          <DialogActions sx={{ p: 2, gap: 1 }}>
            <Button
              variant="outlined"
              onClick={handleRefreshLogs}
              startIcon={<Refresh />}
              disabled={status.loading}
            >
              Refresh
            </Button>
            <Button
              variant="contained"
              onClick={handleCloseModal}
            >
              Close
            </Button>
          </DialogActions>
        </Dialog>
      </IonContent>
    </IonPage>
  );
};

export default StockStatus;